export const SOFTWARE_CREATION_ENTITY_ROUTING_QUERY_KEY =
  "software-creation-legal-entity-routing";

const AI_GOVERNANCE_USER_KEYS = [
  "defaultAiGovernanceUser",
  "default_ai_governance_user",
];

const IT_SECURITY_USER_KEYS = [
  "defaultItSecurityUser",
  "default_it_security_user",
];

const ROUTING_DEFAULT_KEYS = [
  ...AI_GOVERNANCE_USER_KEYS,
  ...IT_SECURITY_USER_KEYS,
];

const toUserId = (value) => {
  if (value == null || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const hasEmbeddedRoutingDefaults = (entity) => {
  if (!entity || typeof entity !== "object") {
    return false;
  }

  if (
    entity.creation_defaults &&
    typeof entity.creation_defaults === "object" &&
    !Array.isArray(entity.creation_defaults)
  ) {
    return true;
  }

  return ROUTING_DEFAULT_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(entity, key)
  );
};

export const unwrapRoutingPayload = (response) => {
  if (!response || typeof response !== "object") {
    return null;
  }

  if (Array.isArray(response.data)) {
    return {};
  }

  if (response.data && typeof response.data === "object") {
    return response.data;
  }

  return response;
};

const pickRawUserId = (source, keys) => {
  if (!source || typeof source !== "object") {
    return null;
  }

  for (const key of keys) {
    const value = source[key];

    if (value && typeof value === "object" && !Array.isArray(value)) {
      const nestedId = toUserId(value.id);
      if (nestedId != null) {
        return nestedId;
      }
      continue;
    }

    const parsed = toUserId(value);
    if (parsed != null) {
      return parsed;
    }
  }

  return null;
};

export const getRoutingUserIdFromPayload = (payload, getRoutingUser, keys) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const candidates = [payload];

  if (
    payload.data &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data)
  ) {
    candidates.push(payload.data);
  }

  for (const candidate of candidates) {
    const routedUser =
      typeof getRoutingUser === "function" ? getRoutingUser(candidate) : null;
    const routedId = toUserId(routedUser?.id);
    if (routedId != null) {
      return routedId;
    }

    const fromDefaults = pickRawUserId(candidate.creation_defaults, keys);
    if (fromDefaults != null) {
      return fromDefaults;
    }

    const fromCandidate = pickRawUserId(candidate, keys);
    if (fromCandidate != null) {
      return fromCandidate;
    }
  }

  return null;
};

export const getAiGovernanceDefaultUserId = (payload, getRoutingUser) =>
  getRoutingUserIdFromPayload(
    payload,
    getRoutingUser,
    AI_GOVERNANCE_USER_KEYS
  );

export const getItSecurityDefaultUserId = (payload, getRoutingUser) =>
  getRoutingUserIdFromPayload(
    payload,
    getRoutingUser,
    IT_SECURITY_USER_KEYS
  );

export const resolveConsensusUserId = (userIds) => {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    return null;
  }

  const normalized = userIds.map((id) => toUserId(id));
  const first = normalized[0];
  const allMatch = normalized.every((id) => id === first);

  return allMatch ? first : null;
};

export const findMemberOption = (options, userId) => {
  const normalizedId = toUserId(userId);

  if (normalizedId == null || !Array.isArray(options)) {
    return null;
  }

  return (
    options.find((option) => toUserId(option?.value) === normalizedId) || null
  );
};

export const resolveDefaultOption = ({
  payloads,
  getUserId,
  memberOptions,
}) => {
  if (!Array.isArray(payloads) || payloads.length === 0) {
    return null;
  }

  const consensusId = resolveConsensusUserId(payloads.map(getUserId));
  return findMemberOption(memberOptions, consensusId);
};

export const getSelectedLegalEntityIds = (selectedLegalEntities) => {
  const ids = [];

  (selectedLegalEntities || []).forEach((entity) => {
    const id = toUserId(entity?.id);
    if (id != null && id > 0 && !ids.includes(id)) {
      ids.push(id);
    }
  });

  return ids;
};
